import Handlebars from '@sparta.global.vendors/handlebars';
import { badgingHelper } from '@sparta.global.helpers/badging-helper/dist/es6.js';
import { svgHelper } from '@sparta.global.helpers/svg-helper/dist/es6.js';

/* eslint-disable no-console */
const VALID_TEXT_ICON_SIZES = ['small', 'large'];

/**
 * Checks for object type
 * @param {object} data Input data
 * @returns {object} The generated markup wrapped to indicate it should never be escaped.
 */
function isObject(data) {
  const result = data !== null && typeof data !== 'undefined' && typeof data === 'object';
  return result;
}

/**
 * Link helper.
 * @param {object} linkData - Data for the `a` tag.
 * @returns {object} The generated markup wrapped to indicate it should never be escaped.
 */
function link(linkData) {
  // Return empty string if linkData is not provided or linkData is not object.
  if (!linkData || typeof linkData !== 'object') {
    return '';
  }
  const link = linkData;
  link.CLASS = link.CLASS || '';
  link.allowEmptyUrl = link.allowEmptyUrl || false;
  link.linkType = link && link.linkType ? `spa-link--${link.linkType}` : '';
  let badging = '';
  let ariaPressed = '';
  let contentRight = '';
  let contentLeft = '';
  let isTextIcon = '';
  const TEXT_ICON_BASE = 'spa-btn-icon';
  let windowOptions;
  const layerClasses = {
    layer: 'spa-ui-layer-link spa-fn',
    popup: 'spa-fn spa-boa-window'
  };

  // Handling buttons.
  if (link.button) {
    link.CLASS += ' spa-btn';
    const {
      button
    } = link;
    const buttonsConfig = {
      sizes: {
        default: 'spa-btn--small',
        xsmall: 'spa-btn--xsmall',
        small: 'spa-btn--small',
        medium: 'spa-btn--medium',
        large: 'spa-btn--large',
        engagement: 'spa-btn--engagement'
      },
      skins: {
        default: 'spa-btn--primary',
        primary: 'spa-btn--primary',
        secondary: 'spa-btn--secondary',
        reverse: 'spa-btn--reverse',
        disabled: 'spa-btn--disabled'
      },
      expand: {
        small: 'spa-btn--expand-on-small',
        medium: 'spa-btn--expand-on-medium'
      }
    };

    /* serverSideOnly */
    // Checking for configuration mismatches.
    if ((button.width || button.height) && button.size) {
      console.log('You may not set the button size with width and/or height. Choose one.');
    }
    /* serverSideOnly */

    // Handle button skin.
    if (button.skin) {
      link.CLASS += ` ${buttonsConfig.skins[button.skin]}`;
    }

    // Handle expand.
    if (button.expand) {
      const buttonExpandKeys = Object.keys(buttonsConfig.expand);

      /* serverSideOnly */
      if (buttonExpandKeys.indexOf(button.expand) === -1) {
        console.log(`Button expand property may only be set as ${buttonExpandKeys.join(', ')}.`);
      }
      /* serverSideOnly */

      link.CLASS += ` ${buttonsConfig.expand[button.expand]}`;
    }

    // Handle block.
    if (button.block) {
      link.CLASS += ' spa-btn--block';
    }

    // Handle button sizes, except for large since it is default.
    if (button.size) {
      const buttonSizeKeys = Object.keys(buttonsConfig.sizes);
      isTextIcon = button.iconTextPattern || '';

      /* serverSideOnly */
      if (!buttonsConfig.sizes[button.size]) {
        console.log(`Button size property may only be set as ${buttonSizeKeys.join(', ')}.`);
      }
      /* serverSideOnly */

      if (!isTextIcon || VALID_TEXT_ICON_SIZES.indexOf(button.size) !== -1) {
        link.CLASS += ` ${buttonsConfig.sizes[button.size]}`;
      }
    } else {
      link.CLASS += ` ${buttonsConfig.sizes.default}`;
    }
  }

  // Handling guillemet.
  if (link.guillemet) {
    const {
      guillemet
    } = link;
    link.CLASS += ' spa-guillemet';
    if (guillemet === 'left') {
      link.CLASS += ' spa-guillemet--left';
    }
  }

  // Check if `actionType` exists.
  if (link.actionType) {
    // If options are defined...
    if (link.actionType.options) {
      windowOptions = link.actionType.options;
      link.OPTIONS = JSON.stringify(link.actionType.options);
    }

    // Handle info, modal, and task layers.
    if (link.actionType.type === 'layer') {
      link.REL = link.actionType.options.rel;
      link.CLASS += ` ${layerClasses[link.actionType.type]}`;
      if (link.actionType.options && link.actionType.options.interstitialType) {
        // Handle for interstitials.
        link.CLASS += ' spa-ui-layer-interstitial';
      } else if (link.actionType.options && link.actionType.options.layerType) {
        // Tooltip layer.
        link.CLASS += ' spa-ui-layer-tooltip-link';
      }
    }

    // Handle popups.
    if (link.actionType.type === 'popup') {
      /* serverSideOnly */
      // Checking whether the popup window dimensions and size are set.
      if ((windowOptions.width || windowOptions.height) && windowOptions.size) {
        console.log('You may not set the popup size with width and/or height. Choose one.');
      }
      /* serverSideOnly */

      link.CLASS += ` ${layerClasses[link.actionType.type]}`;
    }
  }

  // Add rel attr if target='_blank'.
  if (link.TARGET === '_blank') {
    if (link.actionType && link.actionType.options && link.actionType.options.rel) {
      link.REL = `noopener noreferrer ${link.actionType.options.rel}`;
    } else {
      link.REL = `noopener noreferrer`;
    }
  }

  // Trim leading space on class.
  if (link.CLASS.length) {
    link.CLASS = link.CLASS.replace(/^\s+|\s+$/g, '');
  }

  // Icon and badging
  if (link.icon && isTextIcon) {
    const iconTextObj = link.icon;
    if (isObject(iconTextObj) && Object.keys(iconTextObj).length > 0) {
      const iconNameValue = iconTextObj.name ? iconTextObj.name : '';
      const iconPosition = iconTextObj.position ? iconTextObj.position : 'left';
      const additionalClass = link.classes || '';
      link.CLASS += ` ${TEXT_ICON_BASE} ${TEXT_ICON_BASE}--${iconPosition} ${additionalClass} `;
      if (link.selected) {
        ariaPressed = 'aria-pressed="true"';
        link.CLASS += ` ${TEXT_ICON_BASE}--selected `;
      }

      // Add badging
      if (link.badging) {
        link.CLASS += ' spa-btn-icon--has-badge';
        link.badging.options = link.badging.options || {};
        link.badging.options.position = 'relative';
        badging = badgingHelper(link.badging);
      }
      const iconDataJcr = `${svgHelper({
        type: 'static',
        iconName: iconNameValue,
        classes: 'spa-btn-text-icon',
        focusable: 'false',
        ariaHidden: true
      })}`;
      if (iconPosition === 'right') {
        contentRight = `${iconDataJcr}`;
        contentLeft = `${badging}`;
      } else {
        contentRight = `${badging}`;
        contentLeft = `${iconDataJcr}`;
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('config.icon should be of object type for button text icon');
    }
  }

  // escape special characters in a string
  const escapeString = string => {
    const escape = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#x27;',
      '`': '&#x60;',
      '=': '&#x3D;'
    };
    const badChars = /[&<>'`=]/g;
    const possible = /[&<>'`=]/;
    if (typeof string !== 'string') {
      // don't escape SafeStrings, since they're already safe
      if (string && string.toHTML) {
        return string.toHTML();
      }
      if (string == null) {
        return '';
      }
      if (!string) {
        return string + '';
      }

      // Force a string conversion as this will be done by the append regardless and
      // the regex test will do this transparently behind the scenes, causing issues if
      // an object's to string has escaped characters in it.
      string = '' + string;
    }
    if (!possible.test(string)) {
      return string;
    }
    function escapeChar(chr) {
      return escape[chr];
    }
    return string.replace(badChars, escapeChar);
  };

  // eslint-disable-next-line no-script-url
  const linkUrl = link.URL || (link.allowEmptyUrl ? '' : '#');
  let linkClasses = link.CLASS || '';
  linkClasses += !link.URL && !link.allowEmptyUrl ? ' spa-prevent-default-click' : '';
  linkClasses += link.linkType;
  const attributes = [`href="${linkUrl}"`];
  link.vueOptionsStr && attributes.push(link.vueOptionsStr);
  link.ID && attributes.push(`id="${link.ID}"`);
  link.DATA_URL && attributes.push(`data-href="${link.DATA_URL}"`);
  linkClasses ? attributes.push(`class="${linkClasses}"`) : '';
  link.REL && attributes.push(`rel="${link.REL}"`);
  link.OPTIONS && attributes.push(`data-options='${escapeString(link.OPTIONS)}'`);
  link.TARGET && attributes.push(`target="${link.TARGET}"`);
  link.bactmln && attributes.push(`data-bactmln="${link.bactmln}"`);
  link.tabIndex && attributes.push(`tabindex="${link.tabIndex}"`);
  link.ariaLabel && attributes.push(`aria-label="${link.content} ${link.ariaLabel}"`);
  const output = `<a ${attributes.join(' ')} ${ariaPressed}>${contentLeft}${link.content}${contentRight}</a>`;
  return new Handlebars.SafeString(output);
}

export { link };
